import { useEffect } from 'react';
import { useBlocker } from 'react-router';
import { anyDirty, confirmDiscard, useDirtyStore } from '@/lib/dirty';

/**
 * The two ways out of a screen that no button on it controls: a route change (a header link, the
 * back button) and closing or reloading the tab. Mounted once by `Root`. Buttons that leave an
 * editor — a tab, "Back to roster" — ask `confirmDiscard` themselves; this catches the rest.
 *
 * Only a change of path is blocked. A change of query or hash on the same screen keeps the
 * screen, and with it the draft.
 */
export default function LeaveGuard() {
  const dirty = useDirtyStore((state) => state.sources.size > 0);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      anyDirty() && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    void confirmDiscard().then((leave) => (leave ? blocker.proceed() : blocker.reset()));
  }, [blocker]);

  // The browser draws its own question here; a page cannot word it, only ask for it.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  return null;
}
